import { useInfiniteQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import type { StandardEntityLocalizations } from '@/shared/lib/standardEntityLocalizations'
import type {
  HazardLocalizationFields,
  InformationForUseClauseLocalizationFields,
  RiskReductionMeasureLocalizationFields,
} from '@/shared/lib/standardEntityLocalizations'

export type ProjectRelationItem = {
  value: string | null
  name: string | null
  description: string | null
  relationOptionValue: string | null
}

/** Single information-for-use clause (id required, same as SignificantHazard). */
export type InformationForUseClause = {
  id: string
  number: string
  title?: string
  content?: string
  fullStatement?: string
  sectionNumber?: string
  sectionTitle?: string
  actionable?: boolean
  instructionHandbookSectionKey?: string
  localizations?: StandardEntityLocalizations<InformationForUseClauseLocalizationFields>
}

/** Structured "Information for use" payload (matches server). */
export type InformationForUseStructured = {
  clauses: InformationForUseClause[]
  /** True when extraction confirmed no information-for-use section in the PDF. */
  noRequirements?: boolean
  /** BCP-47 language-region tag detected from extracted clause text. */
  sourceLocale?: string
}

/** Single significant hazard row (matches server; id present in API responses). */
export type SignificantHazard = {
  id: string
  number: string
  description: string
  category?: string
  examplesOfHazardousSituations?: string
  associatedActivity?: string
  relatedDangerZone?: string
  clauseReference?: string
  localizations?: StandardEntityLocalizations<HazardLocalizationFields>
}

export type SignificantHazardsResponse = {
  hazards: SignificantHazard[]
  sourceLocale?: string
}

type ExtractedRiskReductionMeasureBase = {
  id: string
  name: string
  type: 'ISD' | 'SCP' | 'PPE' | 'OI'
  description: string
  clauseReference?: string
  localizations?: StandardEntityLocalizations<RiskReductionMeasureLocalizationFields>
}

export type ExtractedRiskReductionMeasure =
  | (ExtractedRiskReductionMeasureBase & {
      type: 'SCP'
      dependsOnControlSystem: boolean
    })
  | (ExtractedRiskReductionMeasureBase & {
      type: 'ISD' | 'PPE' | 'OI'
      dependsOnControlSystem?: boolean
    })

export type RiskReductionMeasuresPayload = {
  measures: ExtractedRiskReductionMeasure[]
  sourceLocale?: string
}

/** PATCH body: SCP keeps dependsOnControlSystem; other types omit it (route schema is strict). */
export function normalizeRiskReductionMeasuresForPatch(
  measures: ExtractedRiskReductionMeasure[]
): ExtractedRiskReductionMeasure[] {
  return measures.map((measure) => {
    if (measure.type === 'SCP') {
      return {
        ...measure,
        dependsOnControlSystem: measure.dependsOnControlSystem ?? false,
      }
    }
    const { dependsOnControlSystem: _ignored, ...withoutDepends } = measure
    return withoutDepends
  })
}

export type RiskReductionMeasuresResponse = {
  measures: ExtractedRiskReductionMeasure[]
  sourceLocale?: string
}

/** Payload for PATCH significant-hazards; consumer sends hazards for create/update. */
export type SignificantHazardsPayload = {
  hazards: SignificantHazard[]
  sourceLocale?: string
  translationLocales?: string[]
}

export type InformationForUseUpdatePayload = InformationForUseStructured & {
  translationLocales?: string[]
}

export type RiskReductionMeasuresUpdatePayload = RiskReductionMeasuresPayload & {
  translationLocales?: string[]
}

export type Standard = {
  id: string
  reference: string
  title: string
  type: string
}

export type StandardPickerItem = {
  id: string
  reference: string
  title: string
  type: string
}

export type StandardPickerSearchResponse = {
  items: StandardPickerItem[]
  pageInfo: { nextCursor: string | null }
}

export type StandardPickerSearchFilters = {
  query: string
  types?: string[]
  excludeReferences?: string[]
}

const STANDARDS_KEY = 'standards'

export function useSearchCanonicalStandardsInfinite(
  filters: StandardPickerSearchFilters,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery<StandardPickerSearchResponse, Error>({
    queryKey: [STANDARDS_KEY, 'search', filters],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params = {
        q: filters.query,
        cursor: pageParam ?? undefined,
        types:
          filters.types != null && filters.types.length > 0
            ? filters.types.join(',')
            : undefined,
        excludeReferences:
          filters.excludeReferences != null &&
          filters.excludeReferences.length > 0
            ? filters.excludeReferences.join(',')
            : undefined,
      }
      const response = await apiClient.get<StandardPickerSearchResponse>(
        '/v1/standards/search',
        { params }
      )
      return response.data
    },
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
    enabled: (options?.enabled ?? true) && filters.query.length >= 2,
  })
}

